import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bClaim, B2bClaimAttachment, B2bClaimBudget } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { B2bContractPlateOption } from '../b2b-contract-plate-select/b2b-contract-plate-select.component';
import { isAttachmentImage } from '../../../shared/utils/attachment-image.util';

interface BudgetDraft {
  id?: number;
  workshopName: string;
  workshopDocument: string;
  amount: number | null;
  notes: string;
  isWinner: boolean;
  attachments: B2bClaimAttachment[];
  pendingFiles: File[];
}

@Component({
  selector: 'app-b2b-claims-form',
  templateUrl: './b2b-claims-form.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-claims-form.component.css']
})
export class B2bClaimsFormComponent implements OnInit, OnDestroy {
  contractId: number | null = null;
  claimId: number | null = null;
  contractNumber = '';
  contractName = '';
  isLoading = false;
  private pendingPreviewUrls = new WeakMap<File, string>();
  submitting = false;
  uploading = false;
  viewMode = false;
  selectedVehicleId: number | null = null;
  form!: FormGroup;
  budgets: BudgetDraft[] = [];
  claimAttachments: B2bClaimAttachment[] = [];
  pendingClaimFiles: File[] = [];
  detailTab: 'dados' | 'comentarios' = 'dados';

  readonly statusOptions = [
    { value: 'Open', label: 'Aberto' },
    { value: 'InAnalysis', label: 'Em análise' },
    { value: 'Approved', label: 'Aprovado' },
    { value: 'Closed', label: 'Fechado' },
    { value: 'Cancelled', label: 'Cancelado' }
  ];

  get pageTitle(): string {
    if (this.viewMode) return 'Visualizar sinistro';
    return this.claimId ? 'Editar sinistro' : 'Novo sinistro';
  }

  get isApproved(): boolean {
    return (this.form?.get('status')?.value || '') === 'Approved';
  }

  get canEditBudgets(): boolean {
    return !this.viewMode && !this.isApproved;
  }

  get showCommentTab(): boolean {
    return !!this.claimId && !!this.contractId;
  }

  selectTab(tab: 'dados' | 'comentarios'): void {
    this.detailTab = tab;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      plate: [''],
      driverName: [''],
      claimDate: [''],
      state: [''],
      description: [''],
      status: ['Open', Validators.required],
      notes: [''],
      agendaLocation: [''],
      agendaAddress: ['']
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      this.claimId = params['claimId'] ? +params['claimId'] : null;
      this.viewMode = (this.route.snapshot.url.map(s => s.path).includes('view'))
        || this.route.snapshot.data['mode'] === 'view';

      if (!this.contractId) return;
      this.loadContractInfo();

      if (this.claimId) {
        this.loadClaim();
      } else {
        this.form.enable();
        this.clearPendingPreviews();
        this.budgets = [];
        this.claimAttachments = [];
        this.pendingClaimFiles = [];
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadClaim(): void {
    if (!this.contractId || !this.claimId) return;
    this.isLoading = true;
    this.contractsService.getB2bClaimById(this.contractId, this.claimId).subscribe({
      next: (raw) => {
        const item = this.normalize(raw);
        this.patchForm(item);
        if (this.viewMode) {
          this.form.disable();
        } else {
          this.form.enable();
          if (item.status === 'Approved') {
            this.form.get('status')?.disable();
          }
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.errorService.showErrorMessage('Não foi possível carregar o sinistro.');
        this.navigateBack();
      }
    });
  }

  private patchForm(item: B2bClaim): void {
    this.selectedVehicleId = item.vehicleId ?? null;
    this.clearPendingPreviews();
    this.pendingClaimFiles = [];
    this.claimAttachments = [...(item.attachments || [])];
    this.budgets = (item.budgets || []).map(b => ({
      id: b.id,
      workshopName: b.workshopName || '',
      workshopDocument: b.workshopDocument || '',
      amount: b.amount ?? null,
      notes: b.notes || '',
      isWinner: !!b.isWinner,
      attachments: [...(b.attachments || [])],
      pendingFiles: []
    }));
    this.form.patchValue({
      plate: item.plate || '',
      driverName: item.driverName || '',
      claimDate: this.toDateInput(item.claimDate),
      state: item.state || '',
      description: item.description || '',
      status: item.status || 'Open',
      notes: item.notes || '',
      agendaLocation: item.agendaLocation || '',
      agendaAddress: item.agendaAddress || ''
    });
  }

  onPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedVehicleId = option?.vehicleId ?? null;
  }

  addBudget(): void {
    if (!this.canEditBudgets || this.budgets.length >= 3) return;
    this.budgets.push({
      workshopName: '',
      workshopDocument: '',
      amount: null,
      notes: '',
      isWinner: false,
      attachments: [],
      pendingFiles: []
    });
  }

  removeBudget(index: number): void {
    if (!this.canEditBudgets) return;
    this.budgets.splice(index, 1);
  }

  onClaimFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.pendingClaimFiles.push(...Array.from(input.files));
    input.value = '';
  }

  onBudgetFilesSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.budgets[index].pendingFiles.push(...Array.from(input.files));
    input.value = '';
  }

  removePendingClaimFile(i: number): void {
    const [removed] = this.pendingClaimFiles.splice(i, 1);
    this.revokePendingPreview(removed);
  }

  removePendingBudgetFile(budgetIndex: number, fileIndex: number): void {
    const [removed] = this.budgets[budgetIndex].pendingFiles.splice(fileIndex, 1);
    this.revokePendingPreview(removed);
  }

  removeClaimAttachment(att: B2bClaimAttachment): void {
    if (this.viewMode) return;
    if (att.id && this.contractId && this.claimId) {
      this.contractsService.deleteB2bClaimAttachment(this.contractId, this.claimId, att.id).subscribe({
        next: () => {
          this.claimAttachments = this.claimAttachments.filter(a => a !== att);
        }
      });
      return;
    }
    this.claimAttachments = this.claimAttachments.filter(a => a !== att);
  }

  removeBudgetAttachment(budgetIndex: number, att: B2bClaimAttachment): void {
    if (!this.canEditBudgets) return;
    if (att.id && this.contractId && this.claimId) {
      this.contractsService.deleteB2bClaimAttachment(this.contractId, this.claimId, att.id).subscribe({
        next: () => {
          this.budgets[budgetIndex].attachments = this.budgets[budgetIndex].attachments.filter(a => a !== att);
        }
      });
      return;
    }
    this.budgets[budgetIndex].attachments = this.budgets[budgetIndex].attachments.filter(a => a !== att);
  }

  private validateBudgets(): string | null {
    if (this.budgets.length > 3) return 'Máximo de 3 orçamentos.';
    const docs = new Set<string>();
    for (const b of this.budgets) {
      if (!b.workshopName?.trim()) return 'Informe o nome da oficina em cada orçamento.';
      if (b.amount == null || Number.isNaN(Number(b.amount))) return 'Informe o valor de cada orçamento.';
      const digits = (b.workshopDocument || '').replace(/\D/g, '');
      if (digits) {
        if (docs.has(digits)) return 'Cada orçamento deve ter CNPJ/CPF distinto.';
        docs.add(digits);
      }
    }
    return null;
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const budgetError = this.validateBudgets();
    if (budgetError) {
      this.errorService.showErrorMessage(budgetError);
      return;
    }

    const raw = this.form.getRawValue();
    const payload: B2bClaim = {
      b2bContractId: this.contractId,
      vehicleId: this.selectedVehicleId,
      plate: raw.plate || null,
      driverName: raw.driverName || null,
      claimDate: raw.claimDate ? new Date(raw.claimDate).toISOString() : null,
      state: raw.state || null,
      description: raw.description || null,
      status: raw.status || 'Open',
      notes: raw.notes || null,
      agendaLocation: raw.agendaLocation || null,
      agendaAddress: raw.agendaAddress || null,
      budgets: this.budgets.map((b, i) => ({
        id: b.id,
        sortOrder: i,
        workshopName: b.workshopName.trim(),
        workshopDocument: b.workshopDocument?.trim() || null,
        amount: Number(b.amount),
        notes: b.notes || null,
        isWinner: !!b.isWinner,
        attachments: b.attachments || []
      })),
      attachments: this.claimAttachments
    };

    this.submitting = true;
    const req$ = this.claimId
      ? this.contractsService.updateB2bClaim(this.contractId, this.claimId, { ...payload, id: this.claimId })
      : this.contractsService.createB2bClaim(this.contractId, payload);

    req$.subscribe({
      next: (saved) => {
        const id = saved?.id ?? this.claimId;
        if (!id || !this.contractId) {
          this.finishSave();
          return;
        }
        this.claimId = id;
        this.uploadPendingFiles(this.contractId, id, this.normalize(saved));
      },
      error: () => { this.submitting = false; }
    });
  }

  private uploadPendingFiles(contractId: number, claimId: number, saved: B2bClaim): void {
    const jobs: { file: File; budgetId?: number | null }[] = [];
    for (const f of this.pendingClaimFiles) jobs.push({ file: f });

    const savedBudgets = (saved?.budgets || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    this.budgets.forEach((b, i) => {
      const budgetId = b.id ?? savedBudgets[i]?.id ?? null;
      for (const f of b.pendingFiles) jobs.push({ file: f, budgetId });
    });

    if (jobs.length === 0) {
      this.finishSave();
      return;
    }

    this.uploading = true;
    let remaining = jobs.length;
    let failed = false;
    for (const job of jobs) {
      this.contractsService.uploadB2bClaimAttachment(contractId, claimId, job.file, job.budgetId).subscribe({
        next: () => {
          remaining -= 1;
          if (remaining === 0) {
            this.uploading = false;
            if (!failed) this.finishSave();
            else this.submitting = false;
          }
        },
        error: () => {
          failed = true;
          remaining -= 1;
          if (remaining === 0) {
            this.uploading = false;
            this.submitting = false;
            this.errorService.showErrorMessage('Sinistro salvo, mas houve falha no upload de anexo.');
            this.navigateBack();
          }
        }
      });
    }
  }

  private finishSave(): void {
    this.submitting = false;
    this.uploading = false;
    this.errorService.showSuccessMessage('Sinistro salvo com sucesso.');
    this.navigateBack();
  }

  printClaim(): void {
    if (!this.contractId || !this.claimId) return;
    this.contractsService.getB2bClaimById(this.contractId, this.claimId).subscribe({
      next: (full) => this.openPrintWindow(this.normalize(full)),
      error: () => this.errorService.showErrorMessage('Não foi possível carregar o sinistro para impressão.')
    });
  }

  private openPrintWindow(claim: B2bClaim): void {
    const winner = (claim.budgets || []).find(b => b.isWinner);
    const budgetsHtml = (claim.budgets || []).map(b => `
      <tr>
        <td>${this.escape(b.workshopName)}${b.isWinner ? ' <strong>(Vencedor)</strong>' : ''}</td>
        <td>${this.escape(b.workshopDocument || '—')}</td>
        <td>${this.formatMoney(b.amount)}</td>
        <td>${this.escape(b.notes || '—')}</td>
      </tr>
    `).join('') || '<tr><td colspan="4">Nenhum orçamento</td></tr>';

    const claimAtts = (claim.attachments || []).map(a =>
      `<li><a href="${this.escape(a.blobUrl)}" target="_blank">${this.escape(a.fileName || a.blobUrl)}</a></li>`
    ).join('') || '<li>Nenhum</li>';

    const winnerAtts = (winner?.attachments || []).map(a =>
      `<li><a href="${this.escape(a.blobUrl)}" target="_blank">${this.escape(a.fileName || a.blobUrl)}</a></li>`
    ).join('') || '<li>Nenhum</li>';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Sinistro ${claim.id}</title>
      <style>
        body{font-family:Arial,sans-serif;color:#222;margin:24px}
        h1{font-size:20px;margin:0 0 8px} h2{font-size:16px;margin:24px 0 8px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #ccc;padding:8px;text-align:left;font-size:13px}
        th{background:#f5f5f5} .meta{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;font-size:13px}
        .meta div span{display:block;color:#666;font-size:11px;text-transform:uppercase}
        @media print{button{display:none}}
      </style></head><body>
      <button onclick="window.print()">Imprimir</button>
      <h1>Sinistro B2B #${claim.id}</h1>
      <p>Contrato ${this.escape(this.contractNumber)} — ${this.escape(this.contractName)}</p>
      <div class="meta">
        <div><span>Placa</span><strong>${this.escape(claim.plate || '—')}</strong></div>
        <div><span>Condutor</span><strong>${this.escape(claim.driverName || '—')}</strong></div>
        <div><span>Data</span><strong>${this.formatDate(claim.claimDate)}</strong></div>
        <div><span>UF</span><strong>${this.escape(claim.state || '—')}</strong></div>
        <div><span>Status</span><strong>${this.statusLabel(claim.status || '')}</strong></div>
        <div><span>Agenda</span><strong>${this.escape(claim.agendaLocation || '—')}</strong></div>
        <div style="grid-column:1/-1"><span>Endereço agenda</span><strong>${this.escape(claim.agendaAddress || '—')}</strong></div>
        <div style="grid-column:1/-1"><span>Descrição</span><strong>${this.escape(claim.description || '—')}</strong></div>
        <div style="grid-column:1/-1"><span>Observações</span><strong>${this.escape(claim.notes || '—')}</strong></div>
      </div>
      <h2>Orçamentos</h2>
      <table><thead><tr><th>Oficina</th><th>CNPJ/CPF</th><th>Valor</th><th>Notas</th></tr></thead>
      <tbody>${budgetsHtml}</tbody></table>
      <h2>Orçamento vencedor</h2>
      ${winner
        ? `<p><strong>${this.escape(winner.workshopName)}</strong> — ${this.escape(winner.workshopDocument || '—')} — ${this.formatMoney(winner.amount)}</p>
           <p>${this.escape(winner.notes || '')}</p>
           <ul>${winnerAtts}</ul>`
        : '<p><em>Nenhum orçamento vencedor selecionado ainda.</em></p>'}
      <h2>Anexos do sinistro (avarias)</h2>
      <ul>${claimAtts}</ul>
      <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
      </body></html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) {
      this.errorService.showErrorMessage('Permita pop-ups para imprimir.');
      return;
    }
    w.document.write(html);
    w.document.close();
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'sinistros']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalize(dto: any): B2bClaim {
    const budgetsRaw = dto?.budgets ?? dto?.Budgets ?? [];
    const attachmentsRaw = dto?.attachments ?? dto?.Attachments ?? [];
    return {
      id: dto?.id ?? dto?.Id,
      b2bContractId: dto?.b2bContractId ?? dto?.b2BContractId ?? dto?.B2BContractId ?? this.contractId!,
      vehicleId: dto?.vehicleId ?? dto?.VehicleId ?? null,
      b2bContractDriverId: dto?.b2bContractDriverId ?? dto?.b2BContractDriverId ?? dto?.B2BContractDriverId ?? null,
      plate: dto?.plate ?? dto?.Plate ?? null,
      driverName: dto?.driverName ?? dto?.DriverName ?? null,
      claimDate: dto?.claimDate ?? dto?.ClaimDate ?? null,
      state: dto?.state ?? dto?.State ?? null,
      budgetAmount: dto?.budgetAmount ?? dto?.BudgetAmount ?? null,
      description: dto?.description ?? dto?.Description ?? null,
      status: dto?.status ?? dto?.Status ?? 'Open',
      notes: dto?.notes ?? dto?.Notes ?? null,
      agendaLocation: dto?.agendaLocation ?? dto?.AgendaLocation ?? null,
      agendaAddress: dto?.agendaAddress ?? dto?.AgendaAddress ?? null,
      budgets: Array.isArray(budgetsRaw) ? budgetsRaw.map((b: any) => this.normalizeBudget(b)) : [],
      attachments: Array.isArray(attachmentsRaw)
        ? attachmentsRaw
            .map((a: any) => this.normalizeAttachment(a))
            .filter((a: B2bClaimAttachment) => a.b2bClaimBudgetId == null)
        : []
    };
  }

  private normalizeBudget(b: any): B2bClaimBudget {
    const atts = b.attachments ?? b.Attachments ?? [];
    return {
      id: b.id ?? b.Id,
      b2bClaimId: b.b2bClaimId ?? b.B2BClaimId,
      sortOrder: b.sortOrder ?? b.SortOrder ?? 0,
      workshopName: b.workshopName ?? b.WorkshopName ?? '',
      workshopDocument: b.workshopDocument ?? b.WorkshopDocument ?? null,
      amount: Number(b.amount ?? b.Amount ?? 0),
      notes: b.notes ?? b.Notes ?? null,
      isWinner: !!(b.isWinner ?? b.IsWinner),
      attachments: Array.isArray(atts) ? atts.map((a: any) => this.normalizeAttachment(a)) : []
    };
  }

  private normalizeAttachment(a: any): B2bClaimAttachment {
    return {
      id: a.id ?? a.Id,
      b2bClaimId: a.b2bClaimId ?? a.B2BClaimId,
      b2bClaimBudgetId: a.b2bClaimBudgetId ?? a.B2BClaimBudgetId ?? null,
      blobUrl: a.blobUrl ?? a.BlobUrl ?? '',
      fileName: a.fileName ?? a.FileName ?? null,
      contentType: a.contentType ?? a.ContentType ?? null,
      fileSize: a.fileSize ?? a.FileSize ?? null,
      uploadedAt: a.uploadedAt ?? a.UploadedAt ?? null
    };
  }

  isImageAttachment(a: { fileName?: string | null; blobUrl?: string | null; contentType?: string | null } | File): boolean {
    if (a instanceof File) {
      return isAttachmentImage({ fileName: a.name, contentType: a.type });
    }
    return isAttachmentImage(a);
  }

  pendingFilePreviewUrl(file: File): string {
    const cached = this.pendingPreviewUrls.get(file);
    if (cached) return cached;
    const url = URL.createObjectURL(file);
    this.pendingPreviewUrls.set(file, url);
    return url;
  }

  ngOnDestroy(): void {
    this.clearPendingPreviews();
  }

  private clearPendingPreviews(): void {
    this.pendingClaimFiles.forEach(f => this.revokePendingPreview(f));
    this.budgets.forEach(b => b.pendingFiles.forEach(f => this.revokePendingPreview(f)));
  }

  private revokePendingPreview(file?: File): void {
    if (!file) return;
    const url = this.pendingPreviewUrls.get(file);
    if (!url) return;
    URL.revokeObjectURL(url);
    this.pendingPreviewUrls.delete(file);
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  private escape(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }

  formatMoney(value: any): string {
    if (value == null || value === '') return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  statusLabel(value: string): string {
    return this.statusOptions.find(s => s.value === value)?.label || value || '—';
  }
}
